import { Monitor, Moon, Sun } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTheme } from "@/hooks/useTheme";
import { cn } from "@/lib/utils";

/** Topbar theme switcher — Sun/Moon icon swaps with the resolved theme;
 *  the dropdown gives an explicit 3-way pick so "system" stays a first-class
 *  choice (auto-flips when the user changes their OS preference). */
export function ThemeToggle() {
  const { pref, resolved, setPref, toggle } = useTheme();

  const Icon = resolved === "dark" ? Moon : Sun;

  return (
    <DropdownMenu>
      {/* Click = quick toggle (light ↔ dark). Long-press / right-click users
          can use the chevron in the dropdown. The asChild trigger handles
          the popup; the inner Button handles the immediate flip on click. */}
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-9 w-9"
          aria-label={`Theme: ${pref} (currently ${resolved})`}
          title={`Theme: ${pref} (currently ${resolved}). Click to pick.`}
          onContextMenu={(e) => {
            // Right-click does a quick toggle without opening the menu.
            e.preventDefault();
            toggle();
          }}
        >
          <Icon className="h-4 w-4 transition-transform" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-40">
        <DropdownMenuItem
          onSelect={() => setPref("light")}
          className={cn(pref === "light" && "bg-accent text-accent-foreground")}
        >
          <Sun className="mr-2 h-4 w-4" />
          Light
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => setPref("dark")}
          className={cn(pref === "dark" && "bg-accent text-accent-foreground")}
        >
          <Moon className="mr-2 h-4 w-4" />
          Dark
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => setPref("system")}
          className={cn(pref === "system" && "bg-accent text-accent-foreground")}
        >
          <Monitor className="mr-2 h-4 w-4" />
          System
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
